import { tr } from "../../i18n";
import { useEffect, useState } from "react";
import { Search, MailOpen, Mail as MailIcon, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../auth/AuthContext";
import { Badge, EmptyState, fmtDateTime } from "../../components/portal";
const dispSubject = s => {
  if (!s) return s;
  for (const p of ["ERI completed", "Website enquiry"]) {
    if (s.startsWith(p + " - ")) return `${tr(p)} - ${s.slice(p.length + 3)}`;
  }
  return s;
};
export default function AdminMail() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [clients, setClients] = useState([]);
  const load = (s = search, f = filter) => {
    api.get(`/admin/emails?search=${encodeURIComponent(s)}&filter=${f}`).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/admin/clients/simple").then(r => setClients(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  const openMail = async m => {
    setSelected(m);
    if (!m.read) {
      try {
        await api.patch(`/admin/emails/${m.id}`, {
          read: true
        });
        load();
      } catch {
        /* noop */
      }
    }
  };
  const update = async (patch, msg) => {
    try {
      await api.patch(`/admin/emails/${selected.id}`, patch);
      toast.success(msg);
      const r = await api.get(`/admin/emails/${selected.id}`);
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return <div className="mx-auto max-w-6xl" data-testid="admin-mail-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-esaia-ink">{tr("Mail")}</h1>
          <p className="mt-1 text-sm text-esaia-muted">{tr("Communications received through ESAIA for info@esaia.org.")}{" "}{data ? ` ${data.unread} ${tr("unread")}.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[["all", "Inbox"], ["unread", "Unread"], ["archived", "Archive"]].map(([f, label]) => <button key={f} onClick={() => setFilter(f)} data-testid={`mail-filter-${f}`} className={`border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${filter === f ? "border-esaia-blue bg-esaia-blue text-white" : "border-esaia-line bg-white text-esaia-muted hover:text-esaia-ink"}`}>
                {tr(label)}
              </button>)}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-esaia-subtle" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder={tr("Search sender or subject")} data-testid="mail-search-input" className="w-56 border border-esaia-line bg-white py-2.5 pl-9 pr-3 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          {!data ? <p className="text-sm text-esaia-subtle">{tr("Loading...")}</p> : data.items.length === 0 ? <EmptyState message={tr("No emails yet.")} /> : <div className="max-h-[70vh] overflow-y-auto border border-esaia-line bg-white" data-testid="mail-list">
              {data.items.map(m => <button key={m.id} onClick={() => openMail(m)} data-testid={`mail-row-${m.id}`} className={`block w-full border-b border-esaia-line px-5 py-4 text-left transition-colors last:border-0 hover:bg-esaia-canvas ${selected?.id === m.id ? "bg-esaia-blue/5" : ""}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className={`truncate text-sm ${m.read ? "text-esaia-muted" : "font-semibold text-esaia-ink"}`}>
                      {!m.read && <span className="mr-2 inline-block h-1.5 w-1.5 rotate-45 bg-esaia-blue" />}
                      {tr(m.sender_name)}
                    </p>
                    <span className="shrink-0 font-mono text-[10px] text-esaia-subtle">{fmtDateTime(m.received_at)}</span>
                  </div>
                  <p className={`mt-1 truncate text-xs ${m.read ? "text-esaia-subtle" : "font-medium text-esaia-ink"}`}>{dispSubject(m.subject)}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-esaia-subtle">{m.sender_email}</p>
                </button>)}
            </div>}
        </div>

        <div className="lg:col-span-3">
          {!selected ? <EmptyState message={tr("Select an email to read it.")} /> : <div className="border border-esaia-line bg-white" data-testid="mail-detail">
              <div className="border-b border-esaia-line px-6 py-5">
                <h2 className="text-base font-semibold text-esaia-ink">{dispSubject(selected.subject)}</h2>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-esaia-subtle">
                  <span>{tr("From:")}{" "}<span className="font-medium text-esaia-ink">{selected.sender_name} &lt;{selected.sender_email}&gt;</span></span>
                  <span>{fmtDateTime(selected.received_at)}</span>
                  {selected.client_id && <Badge tone="blue">{tr("Client linked")}</Badge>}
                </div>
              </div>
              <div className="whitespace-pre-wrap px-6 py-5 text-sm leading-relaxed text-esaia-muted" data-testid="mail-body">
                {selected.body}
              </div>
              <div className="flex flex-wrap items-center gap-2 border-t border-esaia-line px-6 py-4">
                {selected.read ? <button onClick={() => update({
              read: false
            }, "Marked as unread.")} data-testid="mail-mark-unread" className="inline-flex items-center gap-1.5 border border-esaia-line px-3.5 py-2 text-xs font-semibold text-esaia-muted hover:text-esaia-ink">
                    <MailIcon size={14} />{" "}{tr("Mark as Unread")}{" "}</button> : <button onClick={() => update({
              read: true
            }, "Marked as read.")} data-testid="mail-mark-read" className="inline-flex items-center gap-1.5 border border-esaia-line px-3.5 py-2 text-xs font-semibold text-esaia-muted hover:text-esaia-ink">
                    <MailOpen size={14} />{" "}{tr("Mark as Read")}{" "}</button>}
                {selected.archived ? <button onClick={() => update({
              archived: false
            }, "Restored to inbox.")} data-testid="mail-unarchive" className="inline-flex items-center gap-1.5 border border-esaia-line px-3.5 py-2 text-xs font-semibold text-esaia-muted hover:text-esaia-ink">
                    <ArchiveRestore size={14} />{" "}{tr("Restore")}{" "}</button> : <button onClick={() => update({
              archived: true
            }, "Archived.")} data-testid="mail-archive" className="inline-flex items-center gap-1.5 border border-esaia-line px-3.5 py-2 text-xs font-semibold text-esaia-muted hover:text-esaia-ink">
                    <Archive size={14} />{" "}{tr("Archive")}{" "}</button>}
                <select value={selected.client_id || ""} onChange={e => update({
              client_id: e.target.value || null
            }, "Client link updated.")} data-testid="mail-link-client" className="border border-esaia-line bg-white px-3 py-2 text-xs text-esaia-ink focus:border-esaia-blue focus:outline-none">
                  <option value="">{tr("Link to client...")}</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                </select>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}
